import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { fmtTime, fmtDate, attendeeDayLabel } from "@/lib/format";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Clock, ArrowRight, CalendarDays, Plane, Compass, HelpCircle, Image,
  Bell, ChevronRight, Navigation, Hotel, Car, CheckCircle2, Hourglass,
  Sparkles, PartyPopper, Sun, Users, Wallet, Brain, Trophy,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
function startsInLabel(nowIso, s) {
  if (!nowIso || !s?.date || !s?.start_time) return null;
  const start = new Date(`${s.date}T${s.start_time}:00`);
  const now = new Date(nowIso);
  if (isNaN(start) || isNaN(now)) return null;
  const end = s.end_time ? new Date(`${s.date}T${s.end_time}:00`) : null;
  const diffMin = Math.round((start - now) / 60000);
  if (diffMin <= 0) {
    // Only truly in-progress sessions are "Happening now"; ended ones show nothing.
    if (end && now < end) return "Happening now";
    return null;
  }
  if (diffMin < 60) return `Starts in ${diffMin} min`;
  if (diffMin < 1440) {
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return `Starts in ${h}h${m ? ` ${m}m` : ""}`;
  }
  return null;
}

const TRIP_ICON = { flight: Plane, hotel: Hotel, transfer: Car };
const TRIP_LABEL = { flight: "Flight", hotel: "Hotel", transfer: "Transfer" };

function tripStateStyle(state) {
  // gold + solid check for confirmed, muted hourglass otherwise
  if (state === "confirmed" || state === "available") {
    return { Icon: CheckCircle2, color: "text-[hsl(var(--pc-gold))]" };
  }
  return { Icon: Hourglass, color: "text-[hsl(var(--pc-cream)/0.55)]" };
}

/* ------------------------------------------------------------------ */
/* Home                                                               */
/* ------------------------------------------------------------------ */
export default function Home() {
  const { eventId, attendeeId, previewMode, previewNavigate, myProfile } = useApp();
  const [searchParams] = useSearchParams();
  const simNow = searchParams.get("sim") || null; // read-only UAT preview aid
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const routerNavigate = useNavigate();
  // In Manager Preview, contain navigation to the preview's own tabs so the
  // Manager never leaves the preview / the app's single Router is untouched.
  const navigate = previewMode && previewNavigate ? previewNavigate : routerNavigate;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    api
      .home(eventId, attendeeId, simNow)
      .then((d) => {
        if (active) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [eventId, attendeeId, simNow]);

  if (loading) return <HomeSkeleton />;

  if (failed || !data) {
    return (
      <div className="px-5 pt-20 pb-10">
        <div className="att-card p-6 text-center">
          <div className="font-serif text-2xl text-cream">We&apos;re getting things ready</div>
          <p className="mt-2 text-sm text-cream-dim">
            Your personalized Home will appear here in a moment. Please check back shortly.
          </p>
        </div>
      </div>
    );
  }

  const { attendee, event, greeting_word, journey_stage, countdown_days } = data;
  // Prefer the attendee's self-saved display name; fall back to the official name.
  const firstName = (myProfile?.display_name_is_custom && myProfile.display_name)
    ? myProfile.display_name.split(/\s+/)[0]
    : (attendee?.display_first_name || "there");

  return (
    <div
      className="px-4 pt-16 pb-6 space-y-5"
      data-testid="attendee-home"
      data-journey-stage={journey_stage}
    >
      {/* HEADER */}
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[hsl(var(--pc-gold))]">
          President&apos;s Club 2026
        </div>
        <h1
          data-testid="attendee-home-greeting"
          className="mt-1 font-serif text-[2rem] leading-tight text-cream"
        >
          {greeting_word}, {firstName}
        </h1>
        <div className="mt-1 flex items-center gap-1.5 text-sm text-cream-dim">
          <MapPin className="h-3.5 w-3.5 text-[hsl(var(--pc-gold))]" />
          {event?.destination}
        </div>
        {(attendee?.category || data.guest) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {attendee?.category && (
              <span data-testid="attendee-home-category" className="att-gold-chip">
                <Sparkles className="h-3 w-3" /> {attendee.category}
              </span>
            )}
            {data.guest?.name && (
              <span
                data-testid="attendee-home-guest"
                className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--pc-line))] px-2.5 py-0.5 text-[11px] font-medium text-cream-dim"
              >
                <Users className="h-3 w-3" /> Travelling with {data.guest.name}
              </span>
            )}
          </div>
        )}
      </motion.header>

      {/* STAGE-DRIVEN CONTENT */}
      <AnimatePresence mode="wait">
        <motion.div
          key={journey_stage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="space-y-5"
        >
          {journey_stage === "PRE_TRIP" && (
            <PreTrip data={data} navigate={navigate} countdown={countdown_days} />
          )}
          {journey_stage === "ARRIVAL_DAY" && <ArrivalDay data={data} navigate={navigate} />}
          {journey_stage === "LIVE_EVENT" && <LiveEvent data={data} navigate={navigate} />}
          {journey_stage === "DEPARTURE_DAY" && <DepartureDay data={data} navigate={navigate} />}
          {journey_stage === "POST_EVENT" && <PostEvent data={data} navigate={navigate} />}
        </motion.div>
      </AnimatePresence>

      <QuestHomeCard eventId={eventId} attendeeId={attendeeId} navigate={navigate} />
    </div>
  );
}

/* ================= PC QUEST HOME CARD ================= */
function QuestHomeCard({ eventId, attendeeId, navigate }) {
  const [ov, setOv] = useState(null);
  const [fin, setFin] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    let active = true;
    if (!attendeeId) return;
    api.questVisible(eventId).then((v) => {
      if (!active || !v.visible) { setShow(false); return; }
      setShow(true);
      api.questOverview(eventId, attendeeId).then((d) => active && setOv(d)).catch(() => {});
      api.questFinalResults(eventId, attendeeId).then((d) => active && setFin(d)).catch(() => {});
    }).catch(() => {});
    return () => { active = false; };
  }, [eventId, attendeeId]);

  if (!show || !ov) return null;

  const finalized = fin && fin.finalized;
  const anyOpen = (ov.categories || []).some((c) => c.state === "open");
  const anyScheduled = (ov.categories || []).some((c) => c.state === "scheduled");
  let label, sub, Icon = Brain;
  if (finalized) {
    Icon = Trophy;
    label = "View results";
    sub = fin.my_result?.is_winner ? `You won · ${fin.my_result.prize}` : "Winners are in";
  } else if (ov.paused) {
    label = "View";
    sub = "Paused — back soon";
  } else if (ov.closed) {
    label = "View results";
    sub = "Competition closed";
  } else if (ov.answered === 0 && !anyOpen && anyScheduled) {
    label = "Opens soon";
    sub = "Starts shortly";
  } else if (ov.answered === 0) {
    label = "Start";
    sub = "Begin the challenge";
  } else if (ov.answered < ov.total_questions) {
    label = "Continue";
    sub = `${ov.answered}/${ov.total_questions} answered`;
  } else {
    label = "View leaderboard";
    sub = "All questions answered";
  }
  const pct = ov.total_questions ? Math.round((ov.answered / ov.total_questions) * 100) : 0;

  return (
    <section>
      <SectionTitle>PC Quest</SectionTitle>
      <button
        data-testid="attendee-home-quest-card"
        onClick={() => navigate("/app/quest")}
        className="w-full text-left att-card p-4 border-[hsl(var(--pc-gold)/0.4)] hover:border-[hsl(var(--pc-gold)/0.7)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.35)]">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-lg leading-tight text-cream">{ov.title || "PC Quest"}</div>
            <div className="text-xs text-cream-dim">{sub}</div>
          </div>
          <div className="text-right">
            <div className="font-serif text-xl text-[hsl(var(--pc-gold))]" data-testid="attendee-home-quest-points">{ov.my_points}</div>
            <div className="text-[10px] uppercase tracking-wide text-cream-dim">/ {ov.max_score} pts</div>
          </div>
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-[hsl(var(--pc-cream)/0.1)] overflow-hidden">
          <div className="h-full rounded-full bg-[hsl(var(--pc-gold))]" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2.5 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--pc-gold))]" data-testid="attendee-home-quest-action">
          {label} <ArrowRight className="h-4 w-4" />
        </div>
      </button>
    </section>
  );
}

/* ================= STAGE LAYOUTS ================= */

function PreTrip({ data, navigate, countdown }) {
  const { event, trip_snapshot, next_up, important_update, next_actions, next_up_label } = data;
  const actions = next_actions?.items || [];
  const ACTION_TO = { rsvp: "/app/trip", flight: "/app/trip", guest: "/app/trip", dietary: "/app/trip" };

  return (
    <>
      {/* Countdown */}
      <section
        data-testid="attendee-home-countdown"
        className="att-card overflow-hidden p-6 text-center"
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-cream-dim">
          Counting down to
        </div>
        <div className="mt-3 flex items-end justify-center gap-3">
          <span className="font-serif text-6xl leading-none text-[hsl(var(--pc-gold))]">
            {countdown}
          </span>
          <span className="pb-1 font-serif text-2xl text-cream">
            {countdown === 1 ? "day" : "days"}
          </span>
        </div>
        <div className="mt-2 text-sm font-semibold uppercase tracking-[0.22em] text-cream">
          to {event?.destination?.split(",")[0]}
        </div>
        <div className="mt-1 text-xs text-cream-dim">{event?.date_label}</div>
      </section>

      {/* Next actions — what do I need to do? */}
      <section>
        <SectionTitle>{next_actions?.all_set ? "You're all set" : "What to do next"}</SectionTitle>
        <div data-testid="attendee-home-next-step" className="space-y-2">
          {next_actions?.all_set ? (
            <div className="att-card p-5 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--pc-gold))]" />
              <div><div className="font-serif text-lg text-cream">You're all set</div><p className="text-sm text-cream-dim">Nothing needs your attention right now.</p></div>
            </div>
          ) : (
            actions.map((act) => (
              <button
                key={act.key}
                data-testid={`attendee-home-action-${act.key}`}
                onClick={() => navigate(ACTION_TO[act.key] || "/app/trip")}
                className="w-full att-card p-4 flex items-center justify-between hover:bg-[hsl(var(--pc-surface2)/0.7)] transition-colors"
              >
                <span className="font-semibold text-cream">{act.label}</span>
                <ArrowRight className="h-4 w-4 text-[hsl(var(--pc-gold))]" />
              </button>
            ))
          )}
        </div>
      </section>

      <TripSnapshot snapshot={trip_snapshot} navigate={navigate} personal={data.personal_accommodation} features={data.feature_visibility} />

      {next_up && (
        <section>
          <SectionTitle
            action={<FullAgendaLink navigate={navigate} />}
          >
            {next_up_label || "Upcoming"}
          </SectionTitle>
          <NextUpCard s={next_up} now={data.now} navigate={navigate} compact />
        </section>
      )}

      {important_update && <Announcement a={important_update} navigate={navigate} />}
      <QuickActions navigate={navigate} features={data.feature_visibility} />
    </>
  );
}

function ArrivalDay({ data, navigate }) {
  const { event, trip_snapshot, next_up, important_update } = data;
  return (
    <>
      <section data-testid="attendee-home-welcome" className="att-card p-6 text-center">
        <Sun className="mx-auto h-8 w-8 text-[hsl(var(--pc-gold))]" />
        <div className="mt-3 font-serif text-3xl leading-tight text-cream">
          Welcome to {event?.destination?.split(",")[0]}
        </div>
        <p className="mt-1.5 text-sm text-cream-dim">
          You&apos;ve arrived. Here&apos;s everything you need for today.
        </p>
      </section>

      <PrimaryTripCard type="transfer" snapshot={trip_snapshot} navigate={navigate} />
      <PrimaryTripCard type="hotel" snapshot={trip_snapshot} navigate={navigate} />

      {next_up && (
        <section>
          <SectionTitle action={<FullAgendaLink navigate={navigate} />}>Tonight</SectionTitle>
          <NextUpCard s={next_up} now={data.now} navigate={navigate} />
        </section>
      )}

      {important_update && <Announcement a={important_update} navigate={navigate} />}
      <QuickActions navigate={navigate} features={data.feature_visibility} />
    </>
  );
}

function LiveEvent({ data, navigate }) {
  const { next_up, today, important_update, happening_now } = data;
  const feature = happening_now || next_up;
  const rest = (today || []).filter((s) => !feature || s.id !== feature.id).slice(0, 4);
  return (
    <>
      <section>
        <SectionTitle>{happening_now ? "Happening now" : "Next Up"}</SectionTitle>
        {feature ? (
          <NextUpCard s={feature} now={data.now} navigate={navigate} prominent />
        ) : (
          <div className="att-card p-5 text-sm text-cream-dim">
            No further sessions scheduled for now — enjoy the resort at your leisure.
          </div>
        )}
      </section>

      <section>
        <SectionTitle action={<FullAgendaLink navigate={navigate} />}>Today</SectionTitle>
        <div data-testid="attendee-home-today" className="space-y-2.5">
          {rest.length ? (
            rest.map((s) => <TodayRow key={s.id} s={s} navigate={navigate} />)
          ) : (
            <div className="att-card p-5 text-sm text-cream-dim">
              No events scheduled for the rest of today.
            </div>
          )}
        </div>
      </section>

      {important_update && <Announcement a={important_update} navigate={navigate} />}
      <QuickActions navigate={navigate} features={data.feature_visibility} />
    </>
  );
}

function DepartureDay({ data, navigate }) {
  const { trip_snapshot, important_update, stay, event } = data;
  const transfer = trip_snapshot?.transfer;
  const flight = trip_snapshot?.flight;
  return (
    <>
      <section data-testid="attendee-home-departure" className="att-card p-6">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[hsl(var(--pc-gold))]">
          <Plane className="h-3.5 w-3.5" /> Your Departure
        </div>
        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-3">
            <Car className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--pc-gold))]" />
            <div>
              <div className="font-semibold text-cream">Airport transfer</div>
              <div className="text-sm text-cream-dim">
                {transfer?.state === "confirmed"
                  ? transfer.detail
                  : "Your departure transfer details are being finalized."}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Plane className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--pc-gold))]" />
            <div>
              <div className="font-semibold text-cream">Flight</div>
              <div className="text-sm text-cream-dim">
                {flight?.state === "confirmed"
                  ? flight.detail
                  : "Your flight details will appear here once confirmed."}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Hotel className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--pc-gold))]" />
            <div>
              <div className="font-semibold text-cream">Hotel checkout</div>
              <div className="text-sm text-cream-dim">
                Please complete checkout at {event?.host_resort || "your hotel"} before departure.
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate("/app/trip")}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--pc-gold))]"
        >
          View My Trip <ArrowRight className="h-4 w-4" />
        </button>
      </section>

      {important_update && <Announcement a={important_update} navigate={navigate} />}
      <QuickActions navigate={navigate} features={data.feature_visibility} />
    </>
  );
}

function PostEvent({ data, navigate }) {
  const { event } = data;
  return (
    <>
      <section data-testid="attendee-home-thankyou" className="att-card overflow-hidden p-8 text-center">
        <PartyPopper className="mx-auto h-9 w-9 text-[hsl(var(--pc-gold))]" />
        <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-cream-dim">
          Thank you for being part of
        </div>
        <div className="mt-2 font-serif text-3xl leading-tight text-cream">
          President&apos;s Club 2026
        </div>
        <div className="mt-1 text-sm text-cream-dim">{event?.destination?.split(",")[0]}</div>
        <p className="mx-auto mt-4 max-w-xs text-sm leading-relaxed text-cream-dim">
          We hope you enjoyed the celebration. Event highlights and memories will be shared here soon.
        </p>
      </section>
      <QuickActions navigate={navigate} features={data.feature_visibility} />
    </>
  );
}

/* ================= SHARED PIECES ================= */

function TripSnapshot({ snapshot, navigate, personal, features }) {
  if (!snapshot || (features && features.trip === false)) return null;
  const order = ["flight", "hotel", "transfer"];
  return (
    <section>
      <SectionTitle
        action={
          <button
            onClick={() => navigate("/app/trip")}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--pc-gold))]"
          >
            My Trip <ChevronRight className="h-3.5 w-3.5" />
          </button>
        }
      >
        Your Trip
      </SectionTitle>
      <div data-testid="attendee-home-trip-snapshot" className="att-card divide-y divide-[hsl(var(--pc-line)/0.7)]">
        {order.map((key) => {
          const item = snapshot[key];
          if (!item) return null;
          const Lead = TRIP_ICON[key];
          const { Icon, color } = tripStateStyle(item.state);
          return (
            <div
              key={key}
              data-testid={`attendee-home-trip-${key}`}
              className="flex items-center gap-3 p-4"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]">
                <Lead className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-cream-dim">
                  {TRIP_LABEL[key]}
                </div>
                <div className="truncate font-semibold text-cream">{item.title}</div>
                <div className="truncate text-xs text-cream-dim">{item.detail}</div>
              </div>
              <Icon className={`h-4.5 w-4.5 shrink-0 ${color}`} />
            </div>
          );
        })}
        {personal && (
          <button
            data-testid="attendee-home-personal-accommodation"
            onClick={() => navigate("/app/trip")}
            className="flex w-full items-center gap-3 p-4 text-left"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]">
              <Wallet className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-cream-dim">
                Personal hotel cost
              </div>
              <div className="font-semibold text-cream">{personal.employee_display}</div>
              <div className="truncate text-xs text-cream-dim">{personal.note}</div>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-cream-dim" />
          </button>
        )}
      </div>
    </section>
  );
}

function PrimaryTripCard({ type, snapshot, navigate }) {
  const item = snapshot?.[type];
  if (!item) return null;
  const Lead = TRIP_ICON[type];
  const heading = type === "transfer" ? "Your Airport Transfer" : "Your Stay";
  return (
    <section>
      <SectionTitle>{heading}</SectionTitle>
      <button
        data-testid={`attendee-home-primary-${type}`}
        onClick={() => navigate("/app/trip")}
        className="w-full text-left att-card p-4"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]">
            <Lead className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-cream">{item.title}</div>
            <div className="text-sm text-cream-dim">{item.detail}</div>
          </div>
          <ChevronRight className="h-5 w-5 text-cream-dim" />
        </div>
      </button>
    </section>
  );
}

function NextUpCard({ s, now, navigate, prominent, compact }) {
  const startsIn = startsInLabel(now, s);
  return (
    <button
      data-testid="attendee-home-next-up-card"
      onClick={() => navigate("/app/schedule")}
      className="w-full text-left att-card overflow-hidden"
    >
      {prominent && s.image && (
        <div className="relative h-32 w-full">
          <img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 att-hero-fade" />
          <div className="absolute bottom-3 left-4 right-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--pc-gold))] px-2.5 py-1 text-xs font-bold text-[hsl(var(--pc-ink))]">
              <Clock className="h-3 w-3" /> {fmtTime(s.start_time)}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-cream/90">{attendeeDayLabel(s.attendee_day, s.date)}</span>
            {startsIn && <span className="text-xs text-cream/90">{startsIn}</span>}
          </div>
        </div>
      )}
      <div className="p-4">
        {!prominent && (
          <div className="mb-1 flex items-center gap-2 text-xs text-cream-dim">
            <span className="inline-flex items-center gap-1 font-semibold text-[hsl(var(--pc-gold))]">
              <Clock className="h-3 w-3" /> {fmtTime(s.start_time)}
            </span>
            <span>{attendeeDayLabel(s.attendee_day, s.date)}</span>
          </div>
        )}
        <div data-testid="attendee-home-next-up-title" className="font-serif text-xl leading-tight text-cream">
          {s.title}
        </div>
        <div className="mt-1.5 flex items-center gap-3 text-sm text-cream-dim">
          {s.venue && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {s.venue}
            </span>
          )}
        </div>
        {s.meeting_point && (
          <div className="mt-1 flex items-center gap-1 text-xs text-cream-dim">
            <Navigation className="h-3 w-3" /> {s.meeting_point}
          </div>
        )}
        {prominent && startsIn && (
          <div className="mt-2 text-xs font-semibold text-[hsl(var(--pc-gold))]">{startsIn}</div>
        )}
        {compact && startsIn && (
          <div className="mt-2 text-xs font-semibold text-[hsl(var(--pc-gold))]">{startsIn}</div>
        )}
        <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--pc-gold))]">
          View details <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </button>
  );
}

function TodayRow({ s, navigate }) {
  return (
    <button
      onClick={() => navigate("/app/schedule")}
      className="w-full text-left att-card p-3 flex items-center gap-3"
    >
      {s.image ? (
        <img src={s.image} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
      ) : (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--pc-surface2))]">
          <CalendarDays className="h-5 w-5 text-[hsl(var(--pc-gold))]" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-cream">{s.title}</div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-cream-dim">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {fmtTime(s.start_time)}
          </span>
          {s.venue && (
            <span className="inline-flex items-center gap-1 truncate">
              <MapPin className="h-3 w-3" /> {s.venue}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-cream-dim" />
    </button>
  );
}

function Announcement({ a, navigate }) {
  return (
    <section>
      <SectionTitle>Important Update</SectionTitle>
      <div
        data-testid="attendee-home-important-update"
        onClick={() => navigate(`/app/more?tab=announcements&ann=${a.id}`)}
        className="rounded-2xl border border-[hsl(var(--pc-gold)/0.35)] bg-[hsl(var(--pc-gold)/0.1)] p-4 cursor-pointer hover:bg-[hsl(var(--pc-gold)/0.14)] transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.2)] text-[hsl(var(--pc-gold))]">
            <Bell className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-cream">{a.title}</div>
            {a.body && <p className="mt-0.5 text-sm text-cream-dim">{a.body}</p>}
            <button
              data-testid="attendee-home-important-update-read-more"
              onClick={(e) => { e.stopPropagation(); navigate("/app/more?tab=announcements"); }}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--pc-gold))]"
            >
              All announcements <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

const QUICK_ACTIONS = [
  { label: "My Trip", icon: Plane, to: "/app/trip", feature: "trip" },
  { label: "Agenda", icon: CalendarDays, to: "/app/schedule", feature: "schedule" },
  { label: "Photos", icon: Image, to: "/app/photos", feature: "photos" },
  { label: "Help", icon: HelpCircle, to: "/app/more" },
];

function QuickActions({ navigate, features }) {
  const on = (f) => !f || (features || {})[f] !== false;
  const actions = QUICK_ACTIONS.filter((a) => on(a.feature));
  return (
    <section>
      <SectionTitle>Quick Actions</SectionTitle>
      <div data-testid="attendee-home-quick-actions" className="grid grid-cols-4 gap-2.5">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.label}
              data-testid={`attendee-home-quick-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => navigate(a.to)}
              className="flex flex-col items-center gap-2 rounded-2xl att-glass p-3 transition-colors hover:bg-[hsl(var(--pc-surface2)/0.8)]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.35)]">
                <Icon className="h-4.5 w-4.5" strokeWidth={2} />
              </div>
              <span className="text-[11px] font-semibold leading-tight text-cream text-center">
                {a.label}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SectionTitle({ children, action }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="font-serif text-2xl text-cream">{children}</h2>
      {action}
    </div>
  );
}

function FullAgendaLink({ navigate }) {
  return (
    <button
      onClick={() => navigate("/app/schedule")}
      className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--pc-gold))]"
    >
      Full agenda <ChevronRight className="h-3.5 w-3.5" />
    </button>
  );
}

function HomeSkeleton() {
  return (
    <div className="px-4 pt-16 pb-6 space-y-5" data-testid="attendee-home-loading">
      <div className="space-y-2">
        <div className="h-3 w-40 rounded bg-[hsl(var(--pc-surface))] animate-pulse" />
        <div className="h-9 w-56 rounded bg-[hsl(var(--pc-surface))] animate-pulse" />
      </div>
      <div className="h-36 rounded-2xl bg-[hsl(var(--pc-surface))] animate-pulse" />
      <div className="h-24 rounded-2xl bg-[hsl(var(--pc-surface))] animate-pulse" />
      <div className="h-40 rounded-2xl bg-[hsl(var(--pc-surface))] animate-pulse" />
    </div>
  );
}
